import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';
import store, { 
  persistor, 
  clearPersistedState, 
  persistencePending, 
  persistenceError,
  attemptPersistenceRecovery,
  persistenceSuccess
} from './state';
import { Provider, useSelector, useDispatch } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { addConnectionRequestNotification } from './state/notificationSlice';
import { BrowserRouter } from 'react-router-dom';

// Define a flag to disable notification features - must match the one in other files
const NOTIFICATIONS_ENABLED = false; // Set to false to disable notifications

// Suppress WebSocket errors if notifications are disabled
if (!NOTIFICATIONS_ENABLED) {
  // Override WebSocket constructor to prevent connections when disabled
  const originalWebSocket = window.WebSocket;
  window.WebSocket = function(url, protocols) {
    // Block connections to localhost:3000/ws when disabled
    if (url && url.includes('localhost:3000/ws')) {
      // Return mock WebSocket that does nothing
      return {
        url,
        readyState: 3, // CLOSED
        send: () => {},
        close: () => {},
        onopen: null,
        onclose: null,
        onerror: null,
        onmessage: null
      };
    }
    // For other WebSocket connections, use the original constructor
    return new originalWebSocket(url, protocols);
  };
  // Copy static properties from the original WebSocket
  Object.keys(originalWebSocket).forEach(key => {
    window.WebSocket[key] = originalWebSocket[key];
  });
}

// Enhanced loading component with error handling
const LoadingComponent = () => (
  <div className="flex items-center justify-center h-screen bg-gradient-to-br from-pri-dark to-sec-dark">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pri-yellow"></div>
  </div>
);

// Persistence Monitor Component
const PersistenceMonitor = () => {
  const dispatch = useDispatch();
  const persistenceStatus = useSelector(state => state.persistence.status);
  const persistenceError = useSelector(state => state.persistence.error);
  
  // Handle persistence errors
  useEffect(() => {
    if (persistenceStatus === 'error' && persistenceError) {
      // Attempt recovery without page refresh
      const recoverySuccessful = attemptPersistenceRecovery();
      
      if (!recoverySuccessful) {
        // Show error notification instead of refreshing
        showPersistenceErrorNotification();
      }
    }
  }, [persistenceStatus, persistenceError]);
  
  // Force persistence success after a timeout if still pending
  useEffect(() => {
    if (persistenceStatus === 'pending') {
      const timeoutId = setTimeout(() => {
        dispatch(persistenceSuccess());
      }, 3000); // 3 second timeout
      
      return () => clearTimeout(timeoutId);
    }
  }, [persistenceStatus, dispatch]);
  
  return null; // This is just a monitoring component
};

// Function to show a user-friendly error notification
const showPersistenceErrorNotification = () => {
  // Create and show a notification element
  const notificationContainer = document.createElement('div');
  notificationContainer.className = 'fixed top-4 right-4 bg-red-600 text-white p-4 rounded shadow-lg z-50 max-w-md';
  notificationContainer.innerHTML = `
    <div class="flex items-start">
      <div class="ml-3">
        <h3 class="text-sm font-medium">Session Storage Issue</h3>
        <div class="mt-1 text-sm">
          <p>We're having trouble saving your session data. You may need to log in again if this persists.</p>
        </div>
        <div class="mt-3 flex">
          <button type="button" class="refresh-btn text-sm bg-red-700 px-2 py-1 rounded">
            Refresh Page
          </button>
          <button type="button" class="dismiss-btn ml-3 text-sm px-2 py-1 rounded border border-white">
            Dismiss
          </button>
        </div>
      </div>
    </div>
  `;
  
  document.body.appendChild(notificationContainer);
  
  // Add event listeners
  const refreshBtn = notificationContainer.querySelector('.refresh-btn');
  const dismissBtn = notificationContainer.querySelector('.dismiss-btn');
  
  if (refreshBtn) {
    refreshBtn.addEventListener('click', () => {
      window.location.reload();
    });
  }
  
  if (dismissBtn) {
    dismissBtn.addEventListener('click', () => {
      notificationContainer.remove();
    });
  }
  
  // Auto-remove after 10 seconds
  setTimeout(() => {
    if (document.body.contains(notificationContainer)) {
      notificationContainer.remove();
    }
  }, 10000);
};

// Create a component to handle global socket events
const GlobalSocketListener = () => {
  const dispatch = useDispatch();
  
  useEffect(() => {
    if (!NOTIFICATIONS_ENABLED) {
      return;
    }
    
    const handleSocketConnectionRequest = (event) => {
      try {
        if (event.detail) {
          dispatch(addConnectionRequestNotification({
            request: event.detail
          }));
        }
      } catch (error) {
        // Handle error silently
      }
    };
    
    window.addEventListener('socket-connection-request', handleSocketConnectionRequest);
    
    return () => {
      window.removeEventListener('socket-connection-request', handleSocketConnectionRequest);
    };
  }, [dispatch]);
  
  return null; // This component doesn't render anything
};

// Main App wrapper with persistence
const AppWithPersistence = () => {
  // Initialize persistence status
  useEffect(() => {
    store.dispatch(persistencePending());
  }, []);
  
  return (
    <Provider store={store}>
      <PersistGate 
        loading={<LoadingComponent />} 
        persistor={persistor}
        onBeforeLift={() => {
          store.dispatch(persistencePending());
        }}
        onError={(error) => {
          // Handle error without page refresh
          store.dispatch(persistenceError(error.message || 'Unknown persistence error'));
        }}
      >
        {/* Conditionally render GlobalSocketListener based on NOTIFICATIONS_ENABLED flag */}
        {NOTIFICATIONS_ENABLED && <GlobalSocketListener />}
        <PersistenceMonitor />
        <App />
      </PersistGate>
    </Provider>
  );
};

// Initialize the app with error handling
const initializeApp = () => {
  const rootElement = document.getElementById('root');
  
  if (!rootElement) {
    return;
  }
  
  try {
    const root = ReactDOM.createRoot(rootElement);
    root.render(<AppWithPersistence />);
  } catch (error) {
    // Show error screen
    const errorMessage = document.createElement('div');
    errorMessage.className = 'error-message';
    errorMessage.innerHTML = `
      <h1>Something went wrong</h1>
      <p>We're sorry, but we couldn't load the application.</p>
      <p>Error: ${error.message}</p>
      <button onclick="window.location.reload()">Reload</button>
    `;
    
    rootElement.appendChild(errorMessage);
  }
};

initializeApp();
